import moment from 'moment';

import {
  sequence_repository,
} from '@/repositories';

import {
  LICENSE_CONSTANTS,
  SEQUENCE_STATUS_CONSTANTS,
  SEQUENCE_PER_PAGE_COUNT,
  USER_CONSTANTS,
  LINKEDIN_ACCOUNT_STATUS_CONSTANTS,
} from '@/constants';

const DEFAULT_TIME_ZONE = 'Europe/Paris';

const state = () => ({
  sequence_list: [],

  criteria_sort_list: ['-updated_at'],
  criteria_start_date_lower_boundary: undefined,
  criteria_start_date_upper_boundary: undefined,
  criteria_status_id_list: [],
  criteria_user_id_list: [],
  criteria_creator_user_id_list: [],
  criteria_id_list: [],
  criteria_name: null,
  check_import_targets: {
    existing_contact_count: 0,
    new_contact_count: 0,
    no_direct_mail_count: 0,
  },
  limit: SEQUENCE_PER_PAGE_COUNT,
  offset: 0,
  page: 1,
  sequence_count: null,
});

const getters = {
  criteriaIdList: (state) => state.criteria_id_list,
  criteriaCreatorUserIdList: (state) => state.criteria_creator_user_id_list,
  criteriaSortList: (state) => state.criteria_sort_list,
  criteriaStartDateLowerBoundary: (state) => state.criteria_start_date_lower_boundary,
  criteriaStartDateUpperBoundary: (state) => state.criteria_start_date_upper_boundary,
  criteriaStatusIdList: (state) => state.criteria_status_id_list,
  criteriaUserIdList: (state) => state.criteria_user_id_list,
  criteriaName: (state) => state.criteria_name,
  sequenceList: (state) => state.sequence_list,
  settings: (state) => state.settings,
  checkImportTargets: (state) => state.check_import_targets,
  limit: (state) => state.limit,
  page: (state) => state.page,
  offset: (state) => state.offset,
  sequenceCount: (state) => state.sequence_count,
  formatEligibleSenderListForSequenceV2: () => ({ eligible_sender_list, email_signature_list }) => {
    return eligible_sender_list.map((eligible_sender) => {
      const {
        user,
        sender,
      } = eligible_sender;

      const signature = email_signature_list
        .find((sig) => sig.user_id === user.id);

      return {
        id: sender?.id,
        email_can_be_managed_by_connected_user: eligible_sender
          .email_can_be_managed_by_connected_user,
        signature_can_be_modified_by_connected_user: eligible_sender
          .signature_can_be_modified_by_connected_user,
        linkedin_can_be_managed_by_connected_user: eligible_sender
          .linkedin_can_be_managed_by_connected_user,
        user,
        user_id: user.id,
        organization_id: user.organization.id,
        email: {
          first_name: user.first_name,
          last_name: user.last_name,
          address: sender?.email,
          has_signature: Boolean(signature),
          is_connected: Boolean(sender?.email),
          daily_threshold: sender?.daily_threshold,
          preheat_remain_days: sender?.preheat_remain_days,
          last_email_sent_date: sender?.last_email_sent_date,
          last_preheat_update: sender?.last_preheat_update,
        },
        linkedin: {
          linkedin_invitation_threshold: sender?.linkedin_invitation_threshold,
          linkedin_days_to_expire: sender?.linkedin_days_to_expire,
          is_connected: sender?.linkedin_user_account
            ?.status === LINKEDIN_ACCOUNT_STATUS_CONSTANTS.CONNECTED,
          first_name: sender?.linkedin_user_account?.account_infos?.first_name,
          last_name: sender?.linkedin_user_account?.account_infos?.last_name,
          full_name: sender?.linkedin_user_account?.account_infos?.full_name,
          email: sender?.linkedin_user_account?.account_infos?.email,
          premium: sender?.linkedin_user_account?.account_infos?.premium,
          status: sender?.linkedin_user_account?.status,
        },
      };
    });
  },
};

const actions = {
  getCriteria({ state, rootState }) {
    const user = rootState.user.token_data;

    return {
      id_list: state.criteria_id_list.length > 0
        ? state.criteria_id_list
        : undefined,
      creator_user_id_list: state.criteria_creator_user_id_list.length > 0
        ? state.criteria_creator_user_id_list
        : undefined,
      organization_id: user.organization_id,
      status_id_list: state.criteria_status_id_list.length > 0
        ? state.criteria_status_id_list
        : undefined,
      owner_user_id_list: state.criteria_user_id_list.length > 0
        ? state.criteria_user_id_list
        : undefined,
      name: state ? state.criteria_name : undefined,
      limit: state.limit,
      offset: state.offset,
      sort_list: state.criteria_sort_list,
      start_date_lower_boundary: state.criteria_start_date_lower_boundary === undefined
        ? undefined
        : moment(state.criteria_start_date_lower_boundary).startOf('day'),
      start_date_upper_boundary: state.criteria_start_date_upper_boundary === undefined
        ? undefined
        : moment(state.criteria_start_date_upper_boundary).endOf('day'),
    };
  },

  async getSequencesNotIncludingContact({ commit, dispatch, rootState }, input) {
    const {
      contact_id,
    } = input;

    const organization_id = rootState.user.token_data.organization_id;

    const elgible_sequence_list = [
      SEQUENCE_STATUS_CONSTANTS.DRAFT.ID,
      SEQUENCE_STATUS_CONSTANTS.ONGOING.ID,
      SEQUENCE_STATUS_CONSTANTS.PAUSED.ID,
      SEQUENCE_STATUS_CONSTANTS.FINISHED.ID,
    ];

    const [
      response_all_sequences,
      response_sequences_including_contact,
    ] = await Promise.all([
      dispatch(
        'search',
        {
          limit: 1000,
          organization_id,
          status_id_list: elgible_sequence_list,
          sort_list: ['-updated_at'],
        }
      ),
      sequence_repository.searchSequencesIncludingContacts({
        contact_id_list: [contact_id],
        sequence_status_id_list: elgible_sequence_list,
      }),
    ]);

    const all_sequence_list = response_all_sequences.sequence_list;
    const sequence_including_contact_list = response_sequences_including_contact.sequence_list;

    const sequence_including_contact_id_list = sequence_including_contact_list
      .map((sequence) => sequence.id);

    const sequence_not_including_contact_list = all_sequence_list
      .filter((sequence) => !sequence_including_contact_id_list.includes(sequence.id));

    commit('set_sequence_list', sequence_not_including_contact_list);
  },

  async processSearch({ commit, dispatch }) {
    const criteria = await dispatch('getCriteria');
    const result = await dispatch('search', criteria);
    const settings = await dispatch('getSettings');

    commit('set_sequence_list', result.sequence_list);
    commit('set_sequence_count', result.sequence_count);
    commit('set_settings', settings);
  },

  async loadMoreSequences({ commit, dispatch, state }) {
    const offset = state.sequence_list.length;

    if (offset >= state.sequence_count) {
      return { has_more: false };
    }

    const criteria = await dispatch('getCriteria');
    const result = await dispatch('search', {
      ...criteria,
      offset,
      limit: state.limit,
    });

    commit('append_sequence_list', result.sequence_list);

    const has_more = result.sequence_list.length >= state.limit
      && state.sequence_list.length < state.sequence_count;

    return { has_more };
  },

  // Here is the wrapper around repository search method
  async search(_, criteria) {
    const result = await sequence_repository.searchSequences(criteria);

    return result;
  },

  async searchLight(_, criteria) {
    const result = await sequence_repository.searchSequencesLight(criteria);

    return result;
  },

  async createSequence({ rootState, state }, input) {
    const {
      sequence_name,
      sequence_template_id,
    } = input;

    const organization_id = rootState.user.token_data.organization_id;

    const data = {
      finish_event_mail_replied: true,
      name: sequence_name,
      organization_id,
      sendable_timezone: DEFAULT_TIME_ZONE,
      template_id: sequence_template_id,
    };

    const created_sequence = await sequence_repository
      .createSequence(data);

    if (state.criteria_status_id_list.includes(SEQUENCE_STATUS_CONSTANTS.DRAFT.ID)) {
      state.sequence_list.splice(0, 0, created_sequence);
    }

    return created_sequence;
  },

  async updateSequence(_, sequence_data) {
    const sequence_updated = await sequence_repository.updateSequence(sequence_data);

    return sequence_updated;
  },

  async deleteSequence({ state }, sequence_id) {
    await sequence_repository.deleteSequence(sequence_id);

    const deleted_sequence_index = state.sequence_list
      .findIndex((sequence) => sequence.id === sequence_id);

    if (deleted_sequence_index >= 0) {
      state.sequence_list.splice(deleted_sequence_index, 1);
    }
  },

  async duplicateSequence(_, { sequence_id, duplicate_data }) {
    const result = await sequence_repository.duplicateSequence(sequence_id, duplicate_data);

    return result;
  },

  async getSettings({ commit, rootState }) {
    const result = await sequence_repository.getSettings({
      organization_id_list: [rootState.user.token_data.organization_id],
    });

    commit('set_settings', result.setting_list);

    return result.setting_list;
  },

  async getEligibleSequenceSenderList({ dispatch }, input) {
    const {
      connected_user_license_id,
      connected_user_id,
      organization_user_list,
      can_see_all_senders,
    } = input;

    let sender_list = [];

    sender_list = await dispatch('getSettings');

    let eligible_sender_list = [];

    if (connected_user_license_id === LICENSE_CONSTANTS.SALES_AUTOMATION.ID) {
      const ELIGIBLE_LICENSE_ID_LIST = [
        LICENSE_CONSTANTS.SALES_AUTOMATION.ID,
        LICENSE_CONSTANTS.SENDER.ID,
        LICENSE_CONSTANTS.MARKETING.ID,
        LICENSE_CONSTANTS.SALES_AND_MARKETING.ID,
        LICENSE_CONSTANTS.SALES.ID,
        LICENSE_CONSTANTS.KAM.ID,
      ];

      eligible_sender_list = organization_user_list
        .filter((user) =>
          ELIGIBLE_LICENSE_ID_LIST.includes(user.organization_user_license_list
            ?.at(0)?.license?.id)
          && user.status.id === USER_CONSTANTS.STATUS.ACTIVE.ID
        );
    }

    if (connected_user_license_id === LICENSE_CONSTANTS.SALES_AUTOMATION_V2.ID) {
      if (can_see_all_senders) {
        eligible_sender_list = organization_user_list;
      } else {
        eligible_sender_list = organization_user_list
          .filter((user) => user.id === connected_user_id);
      }
    }

    // Smart campaign V1
    if (connected_user_license_id === LICENSE_CONSTANTS.MARKETING.ID) {
      const ELIGIBLE_LICENSE_ID_LIST = [
        LICENSE_CONSTANTS.MARKETING.ID,
        LICENSE_CONSTANTS.SENDER.ID,
        LICENSE_CONSTANTS.SALES_AUTOMATION.ID,
        LICENSE_CONSTANTS.SALES_AND_MARKETING.ID,
        LICENSE_CONSTANTS.SALES.ID,
        LICENSE_CONSTANTS.KAM.ID,
      ];

      eligible_sender_list = organization_user_list
        .filter((user) =>
          ELIGIBLE_LICENSE_ID_LIST.includes(user.organization_user_license_list
            ?.at(0)?.license?.id)
          && user.status.id === USER_CONSTANTS.STATUS.ACTIVE.ID
        );
    }

    if (
      connected_user_license_id === LICENSE_CONSTANTS.SMART_CAMPAIGN_V2.ID
      || connected_user_license_id === LICENSE_CONSTANTS.SALES_AND_MARKETING_V2.ID
    ) {
      const ELIGIBLE_LICENSE_ID_LIST = [
        LICENSE_CONSTANTS.SENDER_V2.ID,
        LICENSE_CONSTANTS.SALES_AUTOMATION_V2.ID,
      ];

      eligible_sender_list = organization_user_list
        .filter((user) => {

          return ELIGIBLE_LICENSE_ID_LIST.includes(user.organization_user_license_list
            ?.at(0)?.license?.id)
          && user.status.id === USER_CONSTANTS.STATUS.ACTIVE.ID;
        });
    }

    if (connected_user_license_id === LICENSE_CONSTANTS.SALES_AND_MARKETING.ID) {
      const ELIGIBLE_LICENSE_ID_LIST = [
        LICENSE_CONSTANTS.MARKETING.ID,
        LICENSE_CONSTANTS.SENDER.ID,
        LICENSE_CONSTANTS.SALES_AUTOMATION.ID,
        LICENSE_CONSTANTS.SALES_AND_MARKETING.ID,
        LICENSE_CONSTANTS.SALES.ID,
        LICENSE_CONSTANTS.KAM.ID,
      ];

      eligible_sender_list = organization_user_list
        .filter((user) =>
          ELIGIBLE_LICENSE_ID_LIST.includes(user.organization_user_license_list
            ?.at(0)?.license?.id)
          && user.status.id === USER_CONSTANTS.STATUS.ACTIVE.ID
        );
    }

    eligible_sender_list = eligible_sender_list.map((user) => {
      let sender = null;

      sender = sender_list.find((sender) => sender.user_id === user.id);

      const object = {
        user,
        sender,
      };

      if (
        (connected_user_license_id === LICENSE_CONSTANTS.MARKETING.ID)
        || (connected_user_license_id === LICENSE_CONSTANTS.SALES_AND_MARKETING.ID)
      ) {
        object.signature_can_be_modified_by_connected_user = [
          LICENSE_CONSTANTS.SENDER.ID,
          LICENSE_CONSTANTS.SALES_AUTOMATION.ID,
        ].includes(
          user.organization_user_license_list?.at(0)?.license?.id
        );

        object.email_can_be_managed_by_connected_user = [
          LICENSE_CONSTANTS.SENDER.ID,
        ].includes(
          user.organization_user_license_list?.at(0)?.license?.id
        ) || (
          user.id === connected_user_id
        );

        object.linkedin_can_be_managed_by_connected_user = [
          LICENSE_CONSTANTS.SENDER.ID,
        ].includes(
          user.organization_user_license_list?.at(0)?.license?.id
        ) || (
          user.id === connected_user_id
        );

        object.connected_user_can_edit_line = [
          LICENSE_CONSTANTS.SENDER.ID,
          LICENSE_CONSTANTS.SALES.ID,
        ].includes(
          user.organization_user_license_list?.at(0)?.license?.id
        );
      }

      if (
        connected_user_license_id === LICENSE_CONSTANTS.SMART_CAMPAIGN_V2.ID
        || connected_user_license_id === LICENSE_CONSTANTS.SALES_AND_MARKETING_V2.ID
      ) {
        object.signature_can_be_modified_by_connected_user = [
          LICENSE_CONSTANTS.SENDER_V2.ID,
          LICENSE_CONSTANTS.SALES_AUTOMATION_V2.ID,
        ].includes(
          user.organization_user_license_list?.at(0)?.license?.id
        );

        object.email_can_be_managed_by_connected_user = [
          LICENSE_CONSTANTS.SENDER_V2.ID,
        ].includes(
          user.organization_user_license_list?.at(0)?.license?.id
        );

        object.linkedin_can_be_managed_by_connected_user = [
          LICENSE_CONSTANTS.SENDER_V2.ID,
        ].includes(
          user.organization_user_license_list?.at(0)?.license?.id
        ) || (
          user.id === connected_user_id
        );

        object.connected_user_can_edit_line = [
          LICENSE_CONSTANTS.SENDER_V2.ID,
        ].includes(
          user.organization_user_license_list?.at(0)?.license?.id
        );
      }

      if (
        (connected_user_license_id === LICENSE_CONSTANTS.SALES_AUTOMATION.ID)
        || (connected_user_license_id === LICENSE_CONSTANTS.SALES_AUTOMATION_V2.ID)
      ) {
        object.email_can_be_managed_by_connected_user = (
          user.id === connected_user_id
        );
        object.linkedin_can_be_managed_by_connected_user = (
          user.id === connected_user_id
        );
      }

      return object;
    });

    return eligible_sender_list;
  },

  async updateSetting(_, { id, params }) {
    const updatted_setting = await sequence_repository.updateSetting(id, params);

    return updatted_setting;
  },

  async userHasConnectedEmail({ dispatch }, owner_user_id) {
    if (!owner_user_id) {
      return false;
    }

    const settings = await dispatch('getSettings');
    const [ user_setting ] = settings.filter((setting) => setting.user_id === owner_user_id);

    return (user_setting?.email ? true : false);
  },

  async checkImportTargets({ commit }, params) {
    const {
      sequence_id,
      contact_id_list,
      last_targeted_date_lower_boundary,
      is_multichannel,
    } = params;

    const check_import_targets = await sequence_repository
      .checkImportTargets(sequence_id, {
        contact_id_list,
        last_targeted_date_lower_boundary,
        is_multichannel,
      });

    commit('set_check_import_targets', check_import_targets);
  },

  async importTargets(_, params) {
    const {
      sequence_id,
      contact_id_list,
      last_targeted_date_lower_boundary,
      is_multichannel,
    } = params;

    await sequence_repository
      .importTargets(sequence_id, {
        contact_id_list,
        last_targeted_date_lower_boundary,
        is_multichannel,
      });
  },

  async getTemplates() {
    const result = await sequence_repository.getTemplates();

    return result.template_list;
  },

  async sendMailTest(_, mail_data) {
    const result = await sequence_repository.sendMailTest(mail_data);

    return result;
  },

  async getLinkedinReplies(_, sequence_id) {
    const result = await sequence_repository.getLinkedinReplies(sequence_id);

    return result;
  },

  async getSequenceStepList(_, sequence_id) {
    const result = await sequence_repository.searchSequenceSteps(sequence_id);

    return result.step_list;
  },

  async getSchedulerData(_, input) {
    const scheduler_data = await sequence_repository.searchScheduledEmails(input);

    return scheduler_data;
  },

  async getBlocksSenderEvents(_, input) {
    const result = await sequence_repository.getBlocksSenderEvents(input);

    return result.blocks_sender_event_dispatch_list;
  },

  async searchTargetsResponsesStatistics(_, input) {
    const result = await sequence_repository.searchTargetsResponsesStatistics(input);

    return result;
  },

  async searchLinkedinInvitationStatistics(_, input) {
    const result = await sequence_repository.searchLinkedinInvitationStatistics(input);

    return result;
  },
};

const mutations = {
  set_criteria_id_list(state, criteria_id_list) {
    state.criteria_id_list = criteria_id_list;
  },
  set_criteria_creator_user_id_list(state, criteria_creator_user_id_list) {
    state.criteria_creator_user_id_list = criteria_creator_user_id_list;
  },
  set_criteria_start_date_lower_boundary(state, criteria_start_date_lower_boundary) {
    state.criteria_start_date_lower_boundary = criteria_start_date_lower_boundary;
  },
  set_criteria_start_date_upper_boundary(state, criteria_start_date_upper_boundary) {
    state.criteria_start_date_upper_boundary = criteria_start_date_upper_boundary;
  },
  set_criteria_status_id_list(state, criteria_status_id_list) {
    state.criteria_status_id_list = criteria_status_id_list;
  },
  set_criteria_user_id_list(state, criteria_user_id_list) {
    state.criteria_user_id_list = criteria_user_id_list;
  },
  set_criteria_name(state, criteria_name) {
    state.criteria_name = criteria_name;
  },
  set_limit(state, limit) {
    state.limit = limit;
  },
  set_sequence_list(state, sequence_list) {
    state.sequence_list = sequence_list;
  },
  append_sequence_list(state, sequence_list) {
    state.sequence_list.push(...sequence_list);
  },
  set_settings(state, settings) {
    state.settings = settings;
  },
  set_offset(state, offset) {
    state.offset = offset;
  },
  set_page(state, page) {
    state.page = page;
  },
  set_sequence_count(state, sequence_count) {
    state.sequence_count = sequence_count;
  },
  set_check_import_targets(state, check_import_targets) {
    state.check_import_targets = check_import_targets;
  },
  set_eligible_sequence_sender_list(state, eligible_sequence_sender_list) {
    state.eligible_sequence_sender_list = eligible_sequence_sender_list;
  },
};

export default {
  actions,
  getters,
  mutations,
  namespaced: true,
  state,
};
